<template>
  <div class="type-card" :class="{ off: !ds.enabled, unsupported: !ds.supported, empty: !ds.items.length }">
    <div class="tc-head">
      <span class="tc-icon">{{ def.icon }}</span>
      <div class="tc-title">
        <h4>{{ def.label }}</h4>
        <p>{{ def.desc }}</p>
      </div>
      <label v-if="ds.supported && ds.items.length" class="switch" :title="ds.enabled ? 'Importeras' : 'Hoppas över'">
        <input type="checkbox" :checked="ds.enabled" @change="$emit('toggle')" />
        <span class="track"><span class="knob"></span></span>
      </label>
    </div>

    <div v-if="!ds.supported" class="tc-body">
      <span class="tc-num muted">{{ fmt(ds.skippedCount) }}</span>
      <span class="tc-unit">{{ def.unit }} i filen</span>
      <div class="tc-note">Stöds inte än – hoppas över.</div>
    </div>

    <div v-else class="tc-body">
      <span class="tc-num">{{ fmt(counts.selected) }}</span>
      <span class="tc-unit">av {{ fmt(counts.total) }} {{ def.unit }} importeras</span>
      <span v-if="range" class="tc-range">{{ range }}</span>

      <div class="tc-chips">
        <span v-if="counts.new" class="chip ok">{{ fmt(counts.new) }} nya</span>
        <span v-if="counts.update" class="chip info">{{ fmt(counts.update) }} uppdateras</span>
        <span v-if="counts.duplicate" class="chip grey">{{ fmt(counts.duplicate) }} finns redan</span>
        <span v-if="counts.overlap" class="chip warn">{{ fmt(counts.overlap) }} överlappar pass</span>
        <span v-if="counts.invalid" class="chip danger">{{ fmt(counts.invalid) }} ogiltiga</span>
        <span v-if="ds.skippedCount" class="chip grey" :title="`${ds.skippedCount} rader kunde inte tolkas`">{{ fmt(ds.skippedCount) }} rader hoppade</span>
      </div>

      <div v-if="blocking && ds.enabled" class="tc-block">⚠ {{ blocking }}</div>
      <div v-else-if="extra" class="tc-note">{{ extra }}</div>
    </div>

    <div v-if="ds.supported && ds.items.length" class="tc-foot">
      <button class="tc-cta" :class="{ primary: !!blocking }" :disabled="!ds.enabled" @click="$emit('open')">
        {{ cta }} →
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImportTypeCard',
  props: {
    def: { type: Object, required: true },
    ds: { type: Object, required: true },
    counts: { type: Object, required: true },
    blocking: { type: String, default: null },
    cta: { type: String, default: 'Granska' },
    extra: { type: String, default: '' },
    range: { type: String, default: '' },
  },
  emits: ['toggle', 'open'],
  setup() {
    return { fmt: n => Number(n || 0).toLocaleString('sv-SE') };
  },
};
</script>

<style scoped>
.type-card {
  background: var(--gym-surface); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-lg); padding: 1rem 1.1rem;
  display: flex; flex-direction: column; gap: 0.75rem; box-shadow: var(--gym-shadow-sm);
  transition: opacity var(--gym-dur) var(--gym-ease), border-color var(--gym-dur) var(--gym-ease);
  min-width: 0;
}
.type-card.off { opacity: 0.55; }
.type-card.unsupported { opacity: 0.6; border-style: dashed; }

.tc-head { display: flex; align-items: flex-start; gap: 0.7rem; }
.tc-icon { font-size: 1.5rem; line-height: 1; margin-top: 0.1rem; }
.tc-title { flex: 1; min-width: 0; }
.tc-title h4 { margin: 0; font-size: 1rem; color: var(--gym-text); }
.tc-title p { margin: 0.1rem 0 0; font-size: 0.78rem; color: var(--gym-text-muted); }

.tc-body { display: flex; flex-direction: column; gap: 0.45rem; }
.tc-num { font-size: 2rem; font-weight: 800; color: var(--gym-accent); line-height: 1; }
.tc-num.muted { color: var(--gym-text-faint); }
.tc-unit { font-size: 0.82rem; color: var(--gym-text-muted); margin-top: -0.2rem; }
.tc-range { font-size: 0.76rem; color: var(--gym-text-faint); }

.tc-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-top: 0.2rem; }
.chip { font-size: 0.74rem; font-weight: 700; padding: 0.18rem 0.55rem; border-radius: var(--gym-radius-pill); white-space: nowrap; }
.chip.ok { background: var(--gym-success-soft); color: var(--gym-success); }
.chip.info { background: var(--gym-accent-soft); color: var(--gym-accent); }
.chip.grey { background: var(--gym-surface-3); color: var(--gym-text-muted); }
.chip.warn { background: var(--gym-warning-soft); color: var(--gym-warning); }
.chip.danger { background: var(--gym-danger-soft); color: var(--gym-danger); }

.tc-block { font-size: 0.8rem; color: var(--gym-warning); font-weight: 600; }
.tc-note { font-size: 0.78rem; color: var(--gym-text-faint); }

.tc-foot { margin-top: auto; }
.tc-cta {
  width: 100%; background: var(--gym-surface-2); color: var(--gym-accent);
  border: 1.5px solid var(--gym-accent-border); padding: 0.5rem 0.9rem;
  border-radius: var(--gym-radius-sm); font-weight: 700; font-size: 0.88rem; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease);
}
.tc-cta:hover:not(:disabled) { background: var(--gym-accent-soft); }
.tc-cta:active:not(:disabled) { transform: scale(0.99); }
.tc-cta:disabled { opacity: 0.5; cursor: not-allowed; }
.tc-cta.primary { background: var(--gym-accent); color: var(--gym-on-accent); border-color: transparent; }
.tc-cta.primary:hover:not(:disabled) { background: var(--gym-accent-hover); }

/* Toggle */
.switch { position: relative; display: inline-flex; align-items: center; cursor: pointer; flex-shrink: 0; }
.switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.track { width: 40px; height: 22px; border-radius: 11px; background: var(--gym-surface-3); border: 1px solid var(--gym-border-strong); position: relative; transition: background var(--gym-dur) var(--gym-ease); }
.knob { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: var(--gym-text-muted); transition: transform var(--gym-dur) var(--gym-ease-spring), background var(--gym-dur) var(--gym-ease); }
.switch input:checked + .track { background: var(--gym-accent); border-color: transparent; }
.switch input:checked + .track .knob { transform: translateX(18px); background: var(--gym-on-accent); }
.switch input:focus-visible + .track { outline: 2px solid var(--gym-accent-border); outline-offset: 2px; }
</style>
